import { useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import CustomTextbox from '../common/CustomTextbox';
import CustomDropdown from '../common/CustomDropdown';
import './CustomForm.css';
import { USStates } from '../../utils/Constants';

const CustomForm = ({ 
  onSubmit, 
  formData, 
  setFormData, 
  formRef, 
  externalErrors = {}, 
  onErrorsCleared 
}) => {
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Merge external errors with internal errors
  useEffect(() => {
    if (externalErrors && Object.keys(externalErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...externalErrors }));
      // Mark fields with external errors as touched
      const touchedFields = {};
      Object.keys(externalErrors).forEach((key) => {
        touchedFields[key] = true;
      });
      setTouched((prev) => ({ ...prev, ...touchedFields }));
    }
  }, [externalErrors]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
      
      // If this was an external error, notify parent to clear it
      if (externalErrors[name] && onErrorsCleared) {
        const updatedExternalErrors = { ...externalErrors };
        delete updatedExternalErrors[name];
        if (Object.keys(updatedExternalErrors).length === 0) {
          onErrorsCleared();
        }
      }
    }
  };

  const handleBlur = (name) => {
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
    validateField(name, formData[name]);
  };

  const validateField = (name, value) => {
    let error = '';
    const fieldValue = value || '';

    switch (name) {
      case 'firstName':
        if (!fieldValue.trim()) {
          error = 'First Name is required';
        }
        break;
      case 'lastName':
        if (!fieldValue.trim()) {
          error = 'Last Name is required';
        }
        break;
      case 'address':
        if (!fieldValue.trim()) {
          error = 'Address is required';
        }
        break;
      case 'city':
        if (!fieldValue.trim()) {
          error = 'City is required';
        }
        break;
      case 'state':
        if (!fieldValue) {
          error = 'Please select a state';
        }
        break;
      case 'postalCode':
        if (!fieldValue.trim()) {
          error = 'Postal code is required';
        }
        break;
      default:
        break;
    }

    return error;
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Validate all required fields
    ['firstName', 'lastName', 'address', 'city', 'state', 'postalCode'].forEach(
      (field) => {
        const error = validateField(field, formData[field]);
        if (error) {
          newErrors[field] = error;
          isValid = false;
        }
      }
    );
    setErrors(newErrors);
    // Mark all fields as touched to show errors
    setTouched({
      firstName: true,
      lastName: true,
      address: true,
      addressLineTwo: true,
      city: true,
      state: true,
      postalCode: true,
    });

    return isValid;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      // Form is valid, call parent onSubmit
      if (onSubmit) {
        onSubmit();
      }
    }
  };

  return (
    <form
      ref={formRef}
      onSubmit={handleFormSubmit}
      className="custom-form"
      noValidate
    >
      <Row>
        <Col md={6} xs={12}>
          <CustomTextbox
            label="First Name"
            placeholder="First Name"
            name="firstName"
            value={formData?.firstName || ''}
            onChange={handleChange}
            onBlur={() => handleBlur('firstName')}
            required
            showError={touched.firstName && !!errors.firstName}
            errorMessage={errors.firstName}
            disabled={true}
          />
        </Col>
        <Col md={6} xs={12}>
          <CustomTextbox
            label="Last Name"
            placeholder="Last Name"
            name="lastName"
            value={formData?.lastName || ''}
            onChange={handleChange}
            onBlur={() => handleBlur('lastName')}
            required
            showError={touched.lastName && !!errors.lastName}
            errorMessage={errors.lastName}
            disabled={true}
          />
        </Col>
      </Row>

      <Row>
        <Col md={6} xs={12}>
          <CustomTextbox
            label="Address"
            placeholder="Address"
            name="address"
            value={formData?.address || ''}
            onChange={handleChange}
            onBlur={() => handleBlur('address')}
            required
            showError={touched.address && !!errors.address}
            errorMessage={errors.address}
          />
        </Col>
        <Col md={6} xs={12}>
          <CustomTextbox
            label="Address Line 2"
            placeholder="Address Line 2"
            name="addressLineTwo"
            value={formData?.addressLineTwo || ''}
            onChange={handleChange}
            onBlur={() => handleBlur('addressLineTwo')}
            required={false}
            showError={touched.addressLineTwo && !!errors.addressLineTwo}
            errorMessage={errors.addressLineTwo}
          />
        </Col>
      </Row>

      <Row>
        <Col md={6} xs={12}>
          <CustomTextbox
            label="City"
            placeholder="City"
            name="city"
            value={formData?.city || ''}
            onChange={handleChange}
            onBlur={() => handleBlur('city')}
            required
            showError={touched.city && !!errors.city}
            errorMessage={errors.city}
          />
        </Col>
        <Col md={6} xs={12}>
          <CustomDropdown
            label="State"
            placeholder="State"
            name="state"
            value={formData?.state || ''}
            onChange={handleChange}
            onBlur={() => handleBlur('state')}
            options={USStates}
            required
            showError={touched.state && !!errors.state}
            errorMessage={errors.state}
          />
        </Col>
      </Row>

      <Row>
        <Col md={6} xs={12}>
          <CustomTextbox
            label="Postal Code"
            placeholder="Postal Code"
            name="postalCode"
            value={formData?.postalCode || ''}
            onChange={handleChange}
            onBlur={() => handleBlur('postalCode')}
            required
            showError={touched.postalCode && !!errors.postalCode}
            errorMessage={errors.postalCode}
          />
        </Col>
      </Row>
    </form>
  );
};

export default CustomForm;
